import * as THREE from 'three';
import { ImprovedNoise } from 'three/examples/jsm/math/ImprovedNoise.js';

/** Décor autonome : ni monde de jeu, ni connexion réseau, ni textures à télécharger. */
export function startHomePlanet() {
  const canvas = document.querySelector<HTMLCanvasElement>('#home-planet')!;
  let renderer: THREE.WebGLRenderer;
  try { renderer = new THREE.WebGLRenderer({ canvas, alpha: true, antialias: true, powerPreference: 'low-power' }); }
  catch { return; } // Le fond CSS garde la page utilisable sans WebGL.
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.5));
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  const scene = new THREE.Scene();
  const camera = new THREE.PerspectiveCamera(38, 1, .1, 100);
  camera.position.set(0, 0, 7.8);
  scene.add(new THREE.HemisphereLight('#b6e2ff', '#111722', 1.7));
  const sun = new THREE.DirectionalLight('#fff1d0', 3.1);
  sun.position.set(-3, 4, 5); scene.add(sun);
  const planet = new THREE.Group(); planet.rotation.z = .22; scene.add(planet);
  const noise = new ImprovedNoise();
  const geometry = new THREE.IcosahedronGeometry(2.2, 24);
  const positions = geometry.getAttribute('position');
  const colors: number[] = [], point = new THREE.Vector3();
  const sea = new THREE.Color('#225f7a'), shore = new THREE.Color('#b6ad79');
  const green = new THREE.Color('#538264'), mountain = new THREE.Color('#8d9883'), snow = new THREE.Color('#e0ece2');
  for (let i = 0; i < positions.count; i++) {
    point.fromBufferAttribute(positions, i).normalize();
    const { x, y, z } = point;
    const height = noise.noise(x * 1.8 + 11, y * 1.8, z * 1.8) + .23 * noise.noise(x * 5 + 11, y * 5, z * 5);
    const color = sea.clone();
    if (height > .025) {
      color.copy(height < .075 ? shore : green);
      if (height > .3) color.lerp(mountain, Math.min(1, (height - .3) * 3));
    } else color.multiplyScalar(.85 + .2 * (height + .7));
    if (Math.abs(y) > .88 + .05 * noise.noise(x * 7, y, z * 7)) color.lerp(snow, .92);
    colors.push(color.r, color.g, color.b);
    point.multiplyScalar(2.2 + Math.max(0, height - .025) * .13);
    positions.setXYZ(i, point.x, point.y, point.z);
  }
  geometry.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));
  geometry.computeVertexNormals();
  planet.add(new THREE.Mesh(geometry, new THREE.MeshStandardMaterial({ vertexColors: true, roughness: .85, flatShading: true })));
  const clouds = new THREE.Group(); planet.add(clouds);
  const cloudMaterial = new THREE.MeshStandardMaterial({ color: '#e3edf0', transparent: true, opacity: .55, flatShading: true, depthWrite: false });
  const cloudGeometry = new THREE.IcosahedronGeometry(1, 1);
  for (let i = 0; i < 44; i++) {
    const a = i * 2.39996, y = 1 - 2 * (i + .5) / 44, r = Math.sqrt(1 - y * y);
    const cloud = new THREE.Mesh(cloudGeometry, cloudMaterial);
    cloud.position.set(Math.cos(a) * r, y, Math.sin(a) * r).multiplyScalar(2.29);
    cloud.lookAt(cloud.position.clone().multiplyScalar(2));
    cloud.scale.set(.16 + .12 * Math.abs(Math.sin(i * 3)), .065, .035);
    clouds.add(cloud);
  }
  const glow = new THREE.Mesh(new THREE.SphereGeometry(2.27, 48, 32), new THREE.ShaderMaterial({
    transparent: true, depthWrite: false, blending: THREE.AdditiveBlending,
    vertexShader: `varying vec3 n; varying vec3 eye; void main(){vec4 p=modelViewMatrix*vec4(position,1.);n=normalize(normalMatrix*normal);eye=-p.xyz;gl_Position=projectionMatrix*p;}`,
    fragmentShader: `varying vec3 n; varying vec3 eye; void main(){float rim=pow(1.-max(0.,dot(normalize(n),normalize(eye))),3.);gl_FragColor=vec4(.25,.65,.85,rim*.35);}`
  })); planet.add(glow);
  const starPositions: number[] = [];
  for (let i = 0; i < 260; i++) starPositions.push(Math.sin(i * 127.1) * 18, Math.sin(i * 311.7) * 12, -5 - (i % 7));
  const starsGeometry = new THREE.BufferGeometry(); starsGeometry.setAttribute('position', new THREE.Float32BufferAttribute(starPositions, 3));
  scene.add(new THREE.Points(starsGeometry, new THREE.PointsMaterial({ color: '#b9d2df', size: .018, transparent: true, opacity: .6 })));
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  let frame = 0, previous = 0, visible = true, disposed = false;
  const draw = () => renderer.render(scene, camera);
  function tick(time: number) {
    frame = 0;
    if (disposed || document.hidden || !visible || reduced.matches) return;
    if (time - previous >= 1000 / 30) {
      const dt = Math.min(.1, (time - previous) / 1000); previous = time;
      planet.rotation.y += dt * .025; clouds.rotation.y += dt * .006; draw();
    }
    frame = requestAnimationFrame(tick);
  }
  function resume() {
    cancelAnimationFrame(frame); frame = 0; previous = performance.now();
    if (disposed || document.hidden || !visible) return;
    draw(); if (!reduced.matches) frame = requestAnimationFrame(tick);
  }
  function resize() {
    const { width, height } = canvas.getBoundingClientRect();
    renderer.setSize(width, height, false); camera.aspect = width / Math.max(1, height); camera.updateProjectionMatrix();
    planet.position.set(width < 740 ? .7 : 1.25, .6, 0); draw();
  }
  const resizeObserver = new ResizeObserver(resize); resizeObserver.observe(canvas);
  const observer = new IntersectionObserver(([entry]) => { visible = !!entry?.isIntersecting; resume(); }); observer.observe(canvas);
  document.addEventListener('visibilitychange', resume); reduced.addEventListener('change', resume);
  canvas.addEventListener('webglcontextlost', event => { event.preventDefault(); cancelAnimationFrame(frame); disposed = true; });
  canvas.addEventListener('webglcontextrestored', () => { disposed = false; resume(); });
  window.addEventListener('pagehide', () => { cancelAnimationFrame(frame); });
  window.addEventListener('pageshow', resume);
  resize(); resume();
}
